import React from "react"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import moment from "moment"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

// Common Components
import MyCard from "@common-utils/MyCard"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"

// Context & Requests
import { getAlertesCounterDashboardRequest } from "../core/_requests"
import { IGetAlertesCounterDashboardResponseModel } from "../core/_models"

// Routing
import {
     ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_CONSULTANTS_AVEC_DOCS_EMBAUCHE_NON_ENVOYES,
     ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_DDC_PRODUCTION,
     ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_DDC_STRUCTURE,
     ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_MISSION_EXPIRATION,
     ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_PE_EXPIRATION,
} from "../routing/routing-endpoints"

const REACT_QUERY_KEY_GET_ALERTES_COUNTER = "REACT_QUERY_KEY_GET_ALERTES_COUNTER"

// --- SUB-COMPONENT: Single Alert Item ---
interface AlertItemProps {
     count: number
     label: string
     subLabel?: string
     onClick: () => void
     isMaintenance?: boolean
     variant?: "social" | "commercial"
}

const AlertItem = ({ count, label, subLabel, onClick, isMaintenance = false, variant = "social" }: AlertItemProps) => {
     const hasAlerts = count > 0

     // Styles based on state
     const baseColor = hasAlerts ? "danger" : "success"
     const bgColor = hasAlerts ? "bg-light-danger" : "bg-light-success"
     const icon = hasAlerts ? "fa-exclamation" : "fa-check"

     return (
          <div
               className={`card h-100 border border-dashed border-${hasAlerts ? "danger" : "gray-300"} ${bgColor} cursor-pointer hover-elevate-up transition-all`}
               onClick={onClick}
          >
               <div className="card-body p-4 d-flex align-items-center">
                    {/* Icon Box */}
                    <div className={`symbol symbol-40px me-4`}>
                         <div className={`symbol-label bg-white border border-${baseColor}`}>
                              <i className={`fas ${icon} text-${baseColor} fs-4`} />
                         </div>
                    </div>

                    {/* Text Content */}
                    <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                         <span className={`fw-bolder fs-6 text-${hasAlerts ? "gray-800" : "gray-600"} text-truncate`}>{label}</span>
                         {subLabel && <span className="text-muted fs-8 text-truncate">{subLabel}</span>}
                         {isMaintenance && <span className="badge badge-light-warning fw-bold fs-9 mt-1 w-auto align-self-start">Soon...</span>}
                    </div>

                    {/* Counter Badge */}
                    <div className={`d-flex align-items-center ms-2`}>
                         <span className={`badge badge-${baseColor} fs-7 fw-bolder px-3 py-2`}>{count}</span>
                    </div>
               </div>
          </div>
     )
}

// --- MAIN COMPONENT ---
const DashboardAlertes = () => {
     const navigate = useNavigate()

     // NOTE: Added intersection type to include the computed properties if they aren't in the base interface
     const alertesCounterQuery = useQuery<IGetAlertesCounterDashboardResponseModel & { alertesSociales: number; alertesCommerciales: number }, AxiosError>(
          REACT_QUERY_KEY_GET_ALERTES_COUNTER,
          async () =>
               getAlertesCounterDashboardRequest({
                    dateConsultantsMissionsExpiredOuArrivantToExpiration: moment().add(45, "days").format("YYYY-MM-DD"),
                    dateConsultantsPeriodesEssaiExpiredOuArrivantToExpiration: moment().add(30, "days").format("YYYY-MM-DD"),
                    dateClientPropalsExpiredOuArrivantToExpiration: moment().add(15, "days").format("YYYY-MM-DD"),
               }).then(r => ({
                    ...r.data,
                    alertesSociales:
                         r.data.consultantsPeriodesEssaiExpiredOuArrivantToExpiration +
                         r.data.consultantsAvecContratOuAvenantNonSignes +
                         r.data.ddcProductionEnCours +
                         r.data.ddcStructureEnCours,
                    alertesCommerciales:
                         r.data.consultantsAvecDocsEmbaucheNonEnvoyes +
                         (r.data.staffAvecDocsEmbaucheNonEnvoyes ?? 0) +
                         r.data.consultantsMissionsExpiredOuArrivantToExpiration +
                         r.data.bonsCommandesManquants +
                         r.data.clientPropalsExpiredOuArrivantToExpiration +
                         r.data.consultantsOrdresMissionExpiredOuArrivantToExpiration,
               }))
     )

     const isAlertSocialeDdcProductionVisible = (() => {
          return true
     })()

     const isAlertSocialeDdcStructureVisible = (() => {
          return true
     })()

     return (
          <MyCard classNames={"mb-8 shadow-sm"}>
               <MyCard.Header classNames="border-0 pt-5">
                    <div className="d-flex align-items-center">
                         <div className="symbol symbol-40px me-3">
                              <div className="symbol-label bg-light-danger">
                                   <i className="fas fa-bell text-danger fs-2" />
                              </div>
                         </div>
                         <div className="d-flex flex-column">
                              <h3 className="card-title fw-bolder text-gray-800 fs-3 mb-1">Centre d'Alertes</h3>
                              <span className="text-muted fw-bold fs-7">Actions requises et notifications</span>
                         </div>
                    </div>
               </MyCard.Header>

               <MyCard.Body>
                    {alertesCounterQuery.isFetching && (
                         <div className={"d-flex justify-content-center py-10"}>
                              <MySimpleSpinner size={"lg"} />
                         </div>
                    )}

                    {!alertesCounterQuery.isFetching && alertesCounterQuery.isError && (
                         <MyAlert type={"danger"} classNames={"mb-4"}>
                              <div className="d-flex align-items-center justify-content-between w-100">
                                   <span>{alertesCounterQuery.error?.response?.data?.detail || "Erreur lors du chargement des alertes."}</span>
                                   <button className={"btn btn-sm btn-outline btn-outline-danger active"} onClick={() => alertesCounterQuery.refetch()}>
                                        <i className="fas fa-sync me-1"></i> Réessayer
                                   </button>
                              </div>
                         </MyAlert>
                    )}

                    {!alertesCounterQuery.isFetching && !alertesCounterQuery.isError && alertesCounterQuery.data && (
                         <div className="row g-10">
                              {/* --- COLUMN 1: SOCIALES --- */}
                              <div className="col-lg-6">
                                   <div className="d-flex align-items-center mb-5">
                                        <span className="bullet bullet-vertical h-20px bg-primary me-3"></span>
                                        <h4 className="text-gray-800 fw-bold m-0">
                                             Social & RH
                                             {/* FIX: Used non-null assertion (!) because we are inside the 'data &&' guard */}
                                             {alertesCounterQuery.data!.alertesSociales > 0 && (
                                                  <span className="badge badge-light-danger ms-2 fs-8 align-middle">{alertesCounterQuery.data!.alertesSociales} actions</span>
                                             )}
                                        </h4>
                                   </div>

                                   <div className="d-flex flex-column gap-3">
                                        <AlertItem
                                             label="Périodes d'essai"
                                             subLabel="Expirées ou à venir (30j)"
                                             count={alertesCounterQuery.data!.consultantsPeriodesEssaiExpiredOuArrivantToExpiration}
                                             onClick={() => navigate(ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_PE_EXPIRATION)}
                                        />

                                        {isAlertSocialeDdcProductionVisible && (
                                             <AlertItem
                                                  label="DDC (Production)"
                                                  subLabel="En attente de validation"
                                                  count={alertesCounterQuery.data!.ddcProductionEnCours}
                                                  onClick={() => navigate(ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_DDC_PRODUCTION, { state: { etat: "ETAT_EN_COURS" } })}
                                             />
                                        )}

                                        {isAlertSocialeDdcStructureVisible && (
                                             <AlertItem
                                                  label="DDC (Structure)"
                                                  subLabel="En attente de validation"
                                                  count={alertesCounterQuery.data!.ddcStructureEnCours}
                                                  onClick={() =>
                                                       alertesCounterQuery.data!.ddcStructureEnCours > 0 &&
                                                       navigate(ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_DDC_STRUCTURE, { state: { etat: "ETAT_EN_COURS" } })
                                                  }
                                             />
                                        )}

                                        <AlertItem
                                             label="Contrats à signer"
                                             count={alertesCounterQuery.data!.consultantsAvecContratOuAvenantNonSignes}
                                             onClick={() => toast.info("Maintenance en cours ...")}
                                        />

                                        <AlertItem label="Entretiens Annuels" count={0} isMaintenance={true} onClick={() => {}} />
                                   </div>
                              </div>

                              {/* --- COLUMN 2: COMMERCIALES --- */}
                              <div className="col-lg-6">
                                   <div className="d-flex align-items-center mb-5">
                                        <span className="bullet bullet-vertical h-20px bg-warning me-3"></span>
                                        <h4 className="text-gray-800 fw-bold m-0">
                                             Commercial & Admin
                                             {/* FIX: Used non-null assertion (!) */}
                                             {alertesCounterQuery.data!.alertesCommerciales > 0 && (
                                                  <span className="badge badge-light-danger ms-2 fs-8 align-middle">{alertesCounterQuery.data!.alertesCommerciales} actions</span>
                                             )}
                                        </h4>
                                   </div>

                                   <div className="d-flex flex-column gap-3">
                                        <AlertItem
                                             label="Documents d'embauche"
                                             subLabel="Non envoyés (consultants & staff)"
                                             count={
                                                  alertesCounterQuery.data!.consultantsAvecDocsEmbaucheNonEnvoyes +
                                                  (alertesCounterQuery.data!.staffAvecDocsEmbaucheNonEnvoyes ?? 0)
                                             }
                                             onClick={() => navigate(ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_CONSULTANTS_AVEC_DOCS_EMBAUCHE_NON_ENVOYES)}
                                        />

                                        <AlertItem
                                             label="Missions & ODM"
                                             subLabel="Renouvellements nécessaires"
                                             count={alertesCounterQuery.data!.consultantsMissionsExpiredOuArrivantToExpiration}
                                             onClick={() => navigate(ROUTING_ABSOLUTE_PATH_DASHBOARD_ALERTES_MISSION_EXPIRATION)}
                                        />

                                        <AlertItem
                                             label="Bons de Commande"
                                             subLabel="Manquants"
                                             count={alertesCounterQuery.data!.bonsCommandesManquants}
                                             onClick={() => toast.info("Maintenance en cours ...")}
                                        />

                                        <AlertItem
                                             label="Portefeuille Client"
                                             subLabel="Propales à prolonger"
                                             count={alertesCounterQuery.data!.clientPropalsExpiredOuArrivantToExpiration}
                                             onClick={() => toast.info("Maintenance en cours ...")}
                                        />

                                        <AlertItem
                                             label="ODM à signer"
                                             count={alertesCounterQuery.data!.consultantsOrdresMissionExpiredOuArrivantToExpiration}
                                             onClick={() => toast.info("Maintenance en cours ...")}
                                        />
                                   </div>
                              </div>
                         </div>
                    )}
               </MyCard.Body>
          </MyCard>
     )
}

export default DashboardAlertes
